import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/Reveal";
import MaskedLines from "@/components/MaskedLines";
import { Quotes } from "@phosphor-icons/react";

const PORTRAIT = "https://customer-assets-cm19k8pv.emergentagent.net/wingman/d4261172-8f99-4797-b06f-85a2a1113728/attachments/1f85e282dd634c82bd2fa77798af880e_ChatGPT%20Image%2017%20lug%202026%2C%2009_24_35.png";

export default function ChiSiamo() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 100]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      id="chi-siamo"
      ref={ref}
      data-testid="section-chi-siamo"
      className="relative py-32 md:py-40 lg:py-48 overflow-hidden bg-navy-deep"
    >
      {/* Chapter numeral */}
      <div
        aria-hidden
        className="absolute top-16 right-6 md:right-10 font-display italic text-[14rem] md:text-[22rem] leading-none text-gold/[0.05] select-none pointer-events-none"
      >
        02
      </div>

      <motion.div style={{ y: bgY }} aria-hidden className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gold/[0.04] blur-[120px]" />
      </motion.div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-8 lg:gap-16 items-center">
          {/* Portrait column */}
          <div className="col-span-12 lg:col-span-5 order-2 lg:order-1">
            <motion.div
              style={{ y: imgY }}
              className="relative aspect-[4/5] max-w-md mx-auto lg:mx-0"
            >
              {/* Spotlight behind */}
              <div className="absolute -inset-8 spotlight opacity-90 blur-2xl" />

              {/* Frame with clipping */}
              <div className="relative w-full h-full overflow-hidden border border-gold/40" data-testid="chi-siamo-portrait">
                {/* Portrait */}
                <img
                  src={PORTRAIT}
                  alt="Marco Danieli, fondatore MD Consulting"
                  className="w-full h-full object-cover"
                  style={{ filter: "brightness(0.95) contrast(1.02)" }}
                />
                {/* Navy overlay for mood */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-navy-dark/10 to-navy-dark/70" />
                {/* Gold accent lines */}
                <span className="absolute top-4 left-4 w-6 h-px bg-gold" />
                <span className="absolute top-4 left-4 h-6 w-px bg-gold" />
                <span className="absolute bottom-4 right-4 w-6 h-px bg-gold" />
                <span className="absolute bottom-4 right-4 h-6 w-px bg-gold" />
              </div>

              {/* Signature card */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="absolute -bottom-6 -right-4 md:-right-10 bg-navy-dark border border-gold/50 p-5 md:p-6 max-w-[240px]"
                data-testid="chi-siamo-signature"
              >
                <p className="font-display italic text-gold text-xl md:text-2xl leading-none">Marco Danieli</p>
                <p
                  className="mt-2 text-[0.65rem] uppercase text-ink/60 font-sans-crisp"
                  style={{ letterSpacing: "0.25em" }}
                >
                  Founder · CEO Advisor
                </p>
                <div className="hairline my-3" />
                <p className="text-[0.7rem] text-ink/50 font-sans-crisp">
                  Treviso · Veneto · Italia
                </p>
              </motion.div>
            </motion.div>
          </div>

          {/* Text column */}
          <div className="col-span-12 lg:col-span-7 order-1 lg:order-2">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <span className="w-10 h-px bg-gold" />
                <span
                  className="text-[0.7rem] uppercase text-gold font-sans-crisp font-semibold"
                  style={{ letterSpacing: "0.3em" }}
                >
                  Capitolo 02 · Chi Siamo
                </span>
              </div>
            </Reveal>

            <MaskedLines
              as="h2"
              testId="chi-siamo-title"
              className="font-display font-medium tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-6xl text-ink"
              lines={[
                'Oltre vent\'anni in prima linea,',
                '<em class="italic-serif text-gold font-normal">al fianco delle PMI.</em>',
              ]}
            />

            <Reveal delay={0.2}>
              <div className="relative mt-10 md:mt-12">
                <Quotes size={40} weight="fill" className="text-gold/40 absolute -top-4 -left-2" />
                <div className="pl-10 text-lg md:text-xl text-ink/80 leading-relaxed font-sans-crisp space-y-6" data-testid="chi-siamo-body">
                  <p>
                    Ho iniziato la mia carriera come Export Manager, lavorando in
                    tutta Europa e imparando sul campo le dinamiche dei mercati,
                    delle reti distributive e, soprattutto, delle persone.
                  </p>
                  <p>
                    In seguito ho guidato aziende sia come{" "}
                    <span className="text-gold">Manager</span> sia come{" "}
                    <span className="text-gold">Imprenditore</span>, assumendomi
                    in prima persona la responsabilità delle decisioni e delle
                    strategie.
                  </p>
                  <p>
                    Da <span className="text-gold">oltre vent'anni</span> mi occupo
                    di acquisizioni, ristrutturazioni aziendali, licensing
                    internazionale e sviluppo di nuovi mercati, operando in settori
                    diversi: dalla moda allo sport, dall'immobiliare ai servizi
                    pubblici.
                  </p>
                  <p>
                    Quando entro in un'azienda, è molto probabile che abbia già
                    affrontato — e contribuito a risolvere — problemi simili a
                    quelli che si trovano davanti.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 md:gap-10 border-t border-gold/15 pt-10">
                {[
                  { n: "20+", l: "Anni CEO" },
                  { n: "6", l: "Settori" },
                  { n: "M&A", l: "Ristrutturazioni" },
                  { n: "PMI", l: "Nord Est" },
                ].map((s) => (
                  <div key={s.l} data-testid={`chi-siamo-stat-${s.l.toLowerCase().replace(/\s/g, "-")}`}>
                    <p className="font-display italic text-gold text-3xl md:text-4xl leading-none">{s.n}</p>
                    <p
                      className="mt-3 text-[0.68rem] uppercase text-ink/50 font-sans-crisp"
                      style={{ letterSpacing: "0.2em" }}
                    >
                      {s.l}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
